import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { Activity } from 'lucide-react'

const ACTION_ICONS = {
  created: '✨',
  updated: '✏️',
  deleted: '🗑️',
  status_changed: '🔄',
  file_uploaded: '📎',
  commented: '💬',
  completed: '✅',
}

export default function ActivityLog({ consultancyId }) {
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchLogs()
  }, [consultancyId])

  async function fetchLogs() {
    setLoading(true)
    const { data } = await supabase
      .from('activity_log')
      .select('*')
      .eq('consultancy_id', consultancyId)
      .order('created_at', { ascending: false })
      .limit(50)
    setLogs(data ?? [])
    setLoading(false)
  }

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-4 border-brand-500 border-t-transparent" />
    </div>
  )

  if (logs.length === 0) return (
    <div className="text-center py-16 text-gray-400">
      <Activity size={40} className="mx-auto mb-3 opacity-20" />
      <p className="font-medium">Sin actividad registrada</p>
      <p className="text-sm mt-1">Las acciones del equipo aparecerán aquí</p>
    </div>
  )

  // Group by date
  const grouped = logs.reduce((acc, log) => {
    const date = new Date(log.created_at).toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    if (!acc[date]) acc[date] = []
    acc[date].push(log)
    return acc
  }, {})

  return (
    <div className="space-y-6">
      {Object.entries(grouped).map(([date, dayLogs]) => (
        <div key={date}>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3 capitalize">{date}</p>
          <div className="space-y-2">
            {dayLogs.map(log => (
              <div key={log.id} className="flex gap-3 items-start bg-white rounded-xl border border-gray-100 px-4 py-3">
                <span className="text-base flex-shrink-0">{ACTION_ICONS[log.action] ?? '🔔'}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800">
                    <span className="font-medium">{log.user_name ?? 'Alguien'}</span>
                    {' '}{getActionLabel(log)}
                    {log.entity_name && <span className="font-medium"> "{log.entity_name}"</span>}
                  </p>
                  {log.details && <p className="text-xs text-gray-400 mt-0.5">{log.details}</p>}
                  <p className="text-xs text-gray-300 mt-1">
                    {new Date(log.created_at).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

function getActionLabel(log) {
  const labels = {
    created:        { task: 'creó la tarea', section: 'creó la sección', phase: 'creó la etapa', business_unit: 'creó la unidad de negocio', comment: 'comentó en' },
    updated:        { task: 'editó la tarea', section: 'editó la sección', phase: 'editó la etapa', business_unit: 'editó la unidad' },
    deleted:        { task: 'eliminó la tarea', section: 'eliminó la sección', phase: 'eliminó la etapa', business_unit: 'eliminó la unidad' },
    status_changed: { task: 'cambió el estado de', section: 'cambió el estado de', phase: 'cambió el estado de' },
    file_uploaded:  { file: 'subió un archivo en', task: 'subió un archivo en' },
    commented:      { comment: 'dejó un comentario en' },
    completed:      { task: 'completó la tarea', section: 'completó la sección' },
  }
  return labels[log.action]?.[log.entity_type] ?? `${log.action} ${log.entity_type}`
}
